import { toast } from "sonner";
import { syncOfflineMotions, cleanupOfflineMotions } from "@/lib/offlineLiveMedia";

/** Offline action queue — ticket creates/closes made without internet are
 * stored locally and replayed automatically once the connection returns.
 * Auth rides on the httpOnly session cookies each portal already uses. */
const KEY = "offline_action_queue_v1";

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
};
const write = (q) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(q));
    window.dispatchEvent(new CustomEvent("offline-queue-changed"));
    return true;
  } catch { return false; }
};

export const isNetworkError = (e) =>
  !e?.response || e?.code === "ERR_NETWORK" || e?.code === "ECONNABORTED";

export const pendingCount = () => read().length;

/** If `e` is a connectivity failure, queue the action and return true. */
export function queueIfOffline(e, { portal, method, path, body, label }) {
  if (!isNetworkError(e)) return false;
  const q = read();
  const item = {
    id: `oq-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    portal, method, path, body, label, at: new Date().toISOString(),
  };
  q.push(item);
  if (!write(q)) {
    if (body && Array.isArray(body.photos) && body.photos.length) {
      item.body = { ...body, photos: [] };
      if (write(q)) {
        toast.warning(`No internet — "${label}" saved WITHOUT photos (too large to store offline). It will send automatically.`, { duration: 8000 });
        return true;
      }
    }
    q.pop();
    write(q);
    toast.error("No internet and this action is too large to save offline. Please retry when connected.");
    return true; // it WAS a network error — caller shouldn't show a generic error
  }
  toast.info(`No internet — "${label}" is saved on this device and will send automatically when you're back online.`, { duration: 8000 });
  return true;
}

const clients = {};
export const registerOfflineClient = (portal, axiosInstance) => { clients[portal] = axiosInstance; };

let flushing = false;
export async function flushOfflineQueue() {
  if (flushing) return;
  const q = read();
  if (!q.length) return;
  flushing = true;
  // Upload any motion clips captured offline FIRST — the axios interceptor in
  // offlineLiveMedia then swaps their real ids into each replayed body.
  try { await syncOfflineMotions(); } catch { /* retried next flush */ }
  const remaining = [];
  let synced = 0;
  for (const item of q) {
    const client = clients[item.portal];
    if (!client) { remaining.push(item); continue; }
    try {
      await client.request({ method: item.method, url: item.path, data: item.body });
      synced += 1;
    } catch (e) {
      if (isNetworkError(e) || e?.response?.status === 401) {
        remaining.push(item); // still offline, or needs a fresh login — keep it
      } else {
        toast.error(`Saved action "${item.label}" was rejected by the server: ${e?.response?.data?.detail || e.message}`, { duration: 8000 });
      }
    }
  }
  write(remaining);
  try { await cleanupOfflineMotions(remaining); } catch { /* best-effort */ }
  flushing = false;
  if (synced) {
    toast.success(`Back online — ${synced} saved action${synced > 1 ? "s" : ""} confirmed in the system.`, { duration: 6000 });
    window.dispatchEvent(new CustomEvent("offline-synced"));
  }
}

export function initOfflineSync() {
  const run = () => flushOfflineQueue();
  window.addEventListener("online", run);
  const iv = setInterval(run, 30000);
  setTimeout(run, 3000);
  return () => { window.removeEventListener("online", run); clearInterval(iv); };
}
